import { useState } from "react";
import { toast } from "sonner";
import { Play, Loader2, FlaskConical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { Panel } from "@/components/core/AppLayout";

const JOBS = [
  { value: "riepilogo", label: "Riepilogo mattutino" },
  { value: "promemoria", label: "Promemoria soci (giorno dopo)" },
  { value: "cliente", label: "Promemoria clienti (giorno dopo)" },
  { value: "sollecito", label: "Sollecito approvazioni ai clienti" },
  { value: "report_mensile", label: "Report mensili in bozza (mese precedente)" },
];
const ESITO = { inviati: "inviato", duplicati: "già inviato (duplicato)", errori: "errore" };

export const JobRunner = ({ onRun }) => {
  const [job, setJob] = useState("cliente");
  const [data, setData] = useState("");
  const [dry, setDry] = useState(true);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/notifiche/esegui", { job, data: data || null, dry_run: dry });
      setRes(r.data);
      toast.success(dry ? "Simulazione completata" : `Job eseguito: ${r.data.inviati} email inviate`);
      onRun?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <Panel className="p-8 core-rise core-delay-3" data-testid="job-runner-panel">
      <h3 className="font-display text-xl font-extrabold text-[#0A1128] flex items-center gap-2 mb-1"><FlaskConical className="h-5 w-5 text-[#E8173A]" />Esegui un job manualmente</h3>
      <p className="text-sm text-slate-600 mb-4">Simula una data per verificare cosa verrebbe inviato. Le email già inviate per la stessa data non vengono duplicate.</p>
      <div className="flex flex-wrap items-center gap-3">
        <Select value={job} onValueChange={setJob}>
          <SelectTrigger className="rounded-xl w-64" data-testid="job-runner-job"><SelectValue /></SelectTrigger>
          <SelectContent>{JOBS.map((j) => <SelectItem key={j.value} value={j.value}>{j.label}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="date" value={data} onChange={(e) => setData(e.target.value)} className="rounded-xl w-44" data-testid="job-runner-date" />
        <label className="flex items-center gap-2 text-sm font-semibold text-[#0A1128] cursor-pointer">
          <Checkbox checked={dry} onCheckedChange={(v) => setDry(!!v)} data-testid="job-runner-dry" />Solo simulazione (non invia)
        </label>
        <Button onClick={run} disabled={busy} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid="job-runner-run">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Play className="h-4 w-4 mr-1.5" />}Esegui
        </Button>
      </div>
      {res && (
        <div className="mt-5 rounded-xl bg-[#A6DAFC] p-4 text-sm text-[#0A1128]" data-testid="job-runner-result">
          <div className="font-bold">Data {res.data} · {res.esito} · inviati {res.inviati} · duplicati {res.duplicati} · errori {res.errori} · saltati {res.saltati} · disattivati {res.disattivati}</div>
          <ul className="mt-2 space-y-1">
            {res.dettagli.map((d, i) => (
              <li key={i} data-testid={`job-runner-detail-${i}`}>{d.to || "—"}{d.appuntamento ? ` · ${d.appuntamento}` : ""} · <span className="font-semibold">{ESITO[d.esito] || d.esito}</span>{d.error ? ` · ${d.error}` : ""}</li>
            ))}
            {!res.dettagli.length && <li>Nessuna email da inviare per questa data.</li>}
          </ul>
        </div>
      )}
    </Panel>
  );
};
